/**
 * Inline "verify your email to continue" prompt for a gated action.
 *
 * Presentation only; `useVerifiedAction` owns the decision, poll, cooldown, and
 * auto-proceed. Renders as a dialog over the current screen, so the user never
 * navigates away from the action they started.
 */

import type { ReactElement } from 'react';
import { Anchor, Button } from '@studio/design-system/button';
import { Prompt } from '@studio/design-system/prompt';
import type { GatedAction } from './logic.js';
import { openSupport } from './support.js';

/** What the user was trying to do, phrased to finish "To … , confirm …". */
const ACTION_PHRASES: Record<GatedAction, string> = {
  password_reset: 'change your password',
  invite: 'invite people to this studio',
  billing: 'change your plan or billing'
};

export interface VerifyToContinueProps {
  /** The held action; drives the copy. */
  action: GatedAction;
  /** Address the link goes to; omitted gracefully when unknown. */
  email: string | undefined;
  /** True once the user has sent a link from this prompt. */
  sent: boolean;
  /** Sending is distinct from success; prevent duplicate pending requests. */
  sending?: boolean;
  /** A failed send leaves the prompt open with a retryable error. */
  sendError?: string | undefined;
  /** Remaining resend cooldown in ms (0 → enabled). */
  cooldownRemainingMs: number;
  /** Send (or resend) the verification email. */
  onResend: () => void;
  /** Drop the held action and close the prompt. */
  onCancel: () => void;
}

/** `<VerifyToContinue>` component. */
export const VerifyToContinue = ({
  action,
  email,
  sent,
  sending = false,
  sendError,
  cooldownRemainingMs,
  onResend,
  onCancel
}: VerifyToContinueProps): ReactElement => {
  const seconds = Math.ceil(cooldownRemainingMs / 1000);
  const onCooldown = cooldownRemainingMs > 0;

  let resendLabel = sent ? 'Resend verification email' : 'Send verification email';
  if (onCooldown) {
    resendLabel = `Resend in ${seconds}s`;
  } else if (sending) {
    resendLabel = 'Sending verification email…';
  }

  return (
    <Prompt
      data-stag='data:email-verification.action-prompt'
      onOpenChange={(open): void => {
        if (!open) {
          onCancel();
        }
      }}
      title='Verify your email to continue'
      message={
        <div className='flex flex-col gap-8 text-md text-secondary'>
          <p>
            To {ACTION_PHRASES[action]}, first confirm{' '}
            {email ? <span className='font-demibold text-foreground'>{email}</span> : 'your email'}.
          </p>
          <p>
            {sent
              ? 'Open the link we sent. This continues automatically once you verify.'
              : 'Send a verification link. This continues automatically once you verify.'}
          </p>
          {/* An address that cannot receive mail never gets the link; changing it or support is the way out. */}
          {sent && (
            <p className='text-sm'>Didn&apos;t get it? Check your spam folder, then resend.</p>
          )}
          <p className='text-sm'>Wrong address? Change it in your profile settings.</p>
        </div>
      }
      content={
        <div className='flex flex-col gap-12'>
          {sendError && (
            <p role='alert' className='text-sm text-secondary'>
              {sendError}
            </p>
          )}
          <Button
            data-stag='interactive:email-verification.action-send'
            disabled={sending || onCooldown}
            onClick={onResend}
            size='lg'
            variant='primary'
          >
            {resendLabel}
          </Button>
          <Button onClick={onCancel} size='lg' variant='secondary'>
            Cancel
          </Button>
          <Anchor
            className='text-sm text-secondary transition-colors hover:text-foreground'
            onClick={openSupport}
          >
            Contact support
          </Anchor>
        </div>
      }
    />
  );
};
